import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { ArrowLeft, ArrowRight, HelpCircle, Loader2, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const questions = [
  {
    id: "club_type",
    question: "What club are you looking to upgrade?",
    subtitle: "Let's start with the basics.",
    options: [
      { value: "Driver", label: "Driver", desc: "The big stick off the tee" },
      { value: "Fairway Wood", label: "Fairway Wood", desc: "For long shots from the deck" },
    ],
  },
  {
    id: "current_shaft",
    question: "What shaft are you currently playing?",
    subtitle: "If you know the make and model, type it in. If not, no worries at all.",
    type: "text",
    placeholder: "e.g., Fujikura Ventus Blue 6S, Project X HZRDUS...",
    skipLabel: "I'm not sure / stock shaft",
    skipValue: "Not sure / stock shaft",
  },
  {
    id: "shaft_likes_dislikes",
    question: "How do you feel about your current shaft?",
    subtitle: "Select everything that applies \u2014 the more we know, the better your match.",
    type: "multi",
    options: [
      { value: "Too heavy - I'm fighting it every swing", label: "It's too heavy", desc: "I feel like I'm swinging a telephone pole" },
      { value: "Too light - I can't feel where the clubhead is", label: "It's too light", desc: "No feedback, no sense of the clubhead loading" },
      { value: "Too stiff - I can't load it or get the ball up", label: "Feels too stiff", desc: "Like swinging a steel rod \u2014 zero flex" },
      { value: "Too whippy - I can't control it under pressure", label: "Feels too whippy", desc: "The shaft does its own thing at impact" },
      { value: "Ball flight is too high - I need something lower", label: "Ball goes too high", desc: "Balloons in the wind, kills my distance" },
      { value: "Ball flight is too low - I need more launch", label: "Ball stays too low", desc: "I can't get it airborne enough to carry" },
      { value: "It actually feels pretty good - just exploring options", label: "It's honestly fine", desc: "Just want to see if there's something better" },
      { value: "I've never been fitted - could be completely wrong for me", label: "Never been fitted", desc: "I'm playing whatever came with the club" },
    ],
  },
  {
    id: "brand_preference",
    question: "Any brand preferences?",
    subtitle: "Tell us who you trust, who you'd avoid, or if you're open to anything.",
    type: "brand_pref",
    preferredPlaceholder: "Brands I love or want to try (e.g., Fujikura, Project X, Graphite Design...)",
    avoidedPlaceholder: "Brands I'd rather avoid (e.g., ...)",
    skipLabel: "No preference \u2014 just find me the best shaft",
    skipValue: "No preference",
  },
  {
    id: "main_goal",
    question: "What's the #1 thing you want to change about your game?",
    subtitle: "Be honest \u2014 this is between us and the algorithm.",
    options: [
      { value: "I'm losing distance and need to get it back", label: "I'm losing distance", desc: "The ball just isn't going as far as it used to" },
      { value: "My shots are all over the place - I need consistency", label: "I need consistency", desc: "One shot is great, the next is a mystery" },
      { value: "I want to shape my shots and have more control", label: "I want more control", desc: "I know what I want the ball to do, my equipment won't let me" },
      { value: "My current shaft just doesn't feel right anymore", label: "Something feels off", desc: "Hard to explain, but the feel and feedback aren't there" },
      { value: "I'm building a new setup and want to get it right from the start", label: "Fresh start", desc: "New clubs, new shaft, new chapter" },
    ],
  },
  {
    id: "typical_miss",
    question: "When things go wrong, what does your bad shot look like?",
    subtitle: "Everyone has one. Knowing yours is the key to fixing it.",
    options: [
      { value: "Big slice that bleeds right and costs me fairways", label: "The dreaded slice", desc: "Starts okay, then curves hard right (lefties: left)" },
      { value: "A hook that shows up at the worst possible moments", label: "The snap hook", desc: "Low and left when the pressure's on" },
      { value: "I balloon the ball - way too much spin, no penetration", label: "The balloon ball", desc: "Launches high but goes nowhere in the wind" },
      { value: "Low shots that never get airborne and don't carry", label: "Can't get it up", desc: "Boring trajectory that hits the ground too early" },
      { value: "Honestly, it's a different miss every time", label: "It's unpredictable", desc: "If I knew what was coming, I could plan for it" },
    ],
  },
  {
    id: "dream_ball_flight",
    question: "Picture your perfect shot. What does it look like?",
    subtitle: "Close your eyes. See the ball flight. Now tell us.",
    options: [
      { value: "High and powerful with maximum carry distance", label: "High and booming", desc: "A towering drive that just keeps going" },
      { value: "Penetrating mid-flight that cuts through any wind", label: "Piercing and controlled", desc: "A bullet that holds its line no matter what" },
      { value: "A gentle draw that rolls out on the fairway", label: "The player's draw", desc: "Starts right, turns left, rolls forever" },
      { value: "Straight and reliable - fairway every time", label: "Straight as an arrow", desc: "No drama, just fairways and confidence" },
      { value: "A workable flight I can shape both ways", label: "Total shot shaping", desc: "Draw when I need it, fade when I want it" },
    ],
  },
  {
    id: "swing_speed",
    question: "What's your swing speed?",
    subtitle: "This is the engine that powers everything.",
    hasTooltip: true,
    options: [
      { value: "Under 75 mph", label: "Under 75 mph", desc: "Smooth swinger, finesse over power" },
      { value: "75-85 mph", label: "75\u201385 mph", desc: "Building speed, room to grow" },
      { value: "85-95 mph", label: "85\u201395 mph", desc: "Average male golfer range" },
      { value: "95-105 mph", label: "95\u2013105 mph", desc: "Above average, you've got pop" },
      { value: "105+ mph", label: "105+ mph", desc: "You've got serious speed" },
    ],
  },
  {
    id: "swing_tempo",
    question: "How would you describe your swing tempo?",
    subtitle: "Your tempo determines what kind of shaft will feel like an extension of you.",
    options: [
      { value: "Smooth and rhythmic - I let the club do the work", label: "Smooth and easy", desc: "Think Ernie Els, Freddie Couples" },
      { value: "Quick and aggressive - I go hard after it", label: "Quick and aggressive", desc: "Think Jon Rahm, Brooks Koepka" },
      { value: "Somewhere in between - balanced tempo", label: "Balanced", desc: "A mix of smooth and aggressive" },
      { value: "I'm still working on finding my rhythm", label: "Still finding it", desc: "No shame \u2014 most golfers are" },
    ],
  },
  {
    id: "current_flex",
    question: "What flex are you currently playing?",
    subtitle: "If you're not sure, that's actually a clue \u2014 you might be in the wrong shaft.",
    options: [
      { value: "Ladies", label: "Ladies", desc: "" },
      { value: "Senior", label: "Senior / A-Flex", desc: "" },
      { value: "Regular", label: "Regular", desc: "Most common off the rack" },
      { value: "Stiff", label: "Stiff", desc: "" },
      { value: "X-Stiff", label: "X-Stiff", desc: "For higher swing speeds" },
      { value: "Not sure", label: "Not sure", desc: "We'll figure it out together" },
    ],
  },
  {
    id: "budget",
    question: "What's your budget per shaft?",
    subtitle: "Great shafts exist at every price point. No judgment here.",
    options: [
      { value: "$100-$150", label: "$100\u2013$150", desc: "Solid performance without breaking the bank" },
      { value: "$150-$250", label: "$150\u2013$250", desc: "Where most premium shafts live" },
      { value: "$250-$400", label: "$250\u2013$400", desc: "Tour-proven, top-shelf performance" },
      { value: "$400+", label: "$400+", desc: "The absolute best \u2014 no compromises" },
    ],
  },
  {
    id: "golf_journey",
    question: "Where are you in your golf journey?",
    subtitle: "There's no wrong answer. Every golfer is on their own path.",
    options: [
      { value: "Just getting started and loving it", label: "Just getting started", desc: "The game has its hooks in me" },
      { value: "Breaking 100 and hungry to improve", label: "Chasing 100", desc: "I can see the improvement and I want more" },
      { value: "Solid mid-handicapper chasing single digits", label: "Chasing single digits", desc: "I know my game, I'm refining it" },
      { value: "Low handicap - I know exactly what I need", label: "Low handicap", desc: "I can feel the difference a shaft makes" },
      { value: "Scratch or better - every detail matters", label: "Scratch or better", desc: "Marginal gains are everything" },
    ],
  },
  {
    id: "contact_info",
    question: "Where should we send your results?",
    subtitle: "Get your personalized recommendations emailed to you with direct links to shop. Totally optional.",
    type: "contact",
  },
];

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -300 : 300, opacity: 0 }),
};

export default function QuestionnairePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill || {};

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [answers, setAnswers] = useState(prefill);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [trackingId] = useState(() => {
    const stored = sessionStorage.getItem("quiz_tracking_id");
    if (stored) return stored;
    const id = crypto.randomUUID ? crypto.randomUUID() : `q-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem("quiz_tracking_id", id);
    return id;
  });
  const [attribution] = useState(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem("g4l_attribution") || "{}");
      let source = stored.utm_source || "";
      if (!source && stored.fbclid) source = "meta";
      return { source, campaign: stored.utm_campaign || "" };
    } catch {
      return { source: "", campaign: "" };
    }
  });
  const answersRef = useRef(answers);
  useEffect(() => { answersRef.current = answers; }, [answers]);

  const progress = ((step + 1) / questions.length) * 100;
  const currentQ = questions[step];

  // Resolve options dynamically for budget question based on club type
  const getOptions = (q) => {
    if (q.dynamic && q.optionsByClub) {
      const club = answers.club_type || "";
      const isPremium = ["Driver", "Fairway Wood", "Hybrid"].includes(club);
      return isPremium ? q.optionsByClub.premium : q.optionsByClub.standard;
    }
    return q.options || [];
  };
  const currentOptions = getOptions(currentQ);
  const selectedValue = answers[currentQ.id] || (currentQ.type === "multi" ? [] : currentQ.type === "brand_pref" ? { preferred: "", avoided: "" } : "");

  const questionId = currentQ.id;

  const selectOption = useCallback(
    (value) => {
      setAnswers((prev) => {
        const updated = { ...prev, [questionId]: value };
        if (questionId === "club_type" && prev.club_type !== value) {
          delete updated.budget;
        }
        return updated;
      });
    },
    [questionId]
  );

  const toggleMultiOption = useCallback(
    (value) => {
      setAnswers((prev) => {
        const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
        const exists = current.includes(value);
        const updated = exists
          ? current.filter((v) => v !== value)
          : [...current, value];
        return { ...prev, [questionId]: updated };
      });
    },
    [questionId]
  );

  const handleTextChange = useCallback(
    (e) => {
      setAnswers((prev) => ({ ...prev, [questionId]: e.target.value }));
    },
    [questionId]
  );

  const handleBrandChange = useCallback(
    (field, value) => {
      setAnswers((prev) => {
        const current = prev[questionId] && typeof prev[questionId] === "object" && !Array.isArray(prev[questionId])
          ? prev[questionId]
          : { preferred: "", avoided: "" };
        return { ...prev, [questionId]: { ...current, [field]: value } };
      });
    },
    [questionId]
  );

  const handleSkip = useCallback(
    (skipValue) => {
      setAnswers((prev) => ({ ...prev, [questionId]: skipValue }));
    },
    [questionId]
  );

  // Check if current step has a valid answer
  const hasAnswer = (() => {
    if (currentQ.type === "contact") {
      return true; // Always valid — email is optional
    }
    if (currentQ.type === "multi") {
      return Array.isArray(selectedValue) && selectedValue.length > 0;
    }
    if (currentQ.type === "text") {
      return typeof selectedValue === "string" && selectedValue.trim().length > 0;
    }
    if (currentQ.type === "brand_pref") {
      if (typeof selectedValue === "string") return selectedValue.trim().length > 0; // skip value
      if (typeof selectedValue === "object" && !Array.isArray(selectedValue)) {
        return (selectedValue.preferred || "").trim().length > 0 || (selectedValue.avoided || "").trim().length > 0;
      }
      return false;
    }
    return !!selectedValue;
  })();

  const goNext = useCallback(() => {
    if (!hasAnswer) return;
    if (step < questions.length - 1) {
      setDirection(1);
      const nextStep = step + 1;
      setStep(nextStep);
      // Track partial progress in background using ref to avoid stale closure
      axios.post(`${API}/track/progress`, {
        session_id: trackingId,
        step: nextStep,
        answers: answersRef.current,
      }).catch(() => {});
    }
  }, [hasAnswer, step, trackingId]);

  const goBack = useCallback(() => {
    if (step > 0) {
      setDirection(-1);
      setStep((s) => s - 1);
    } else {
      navigate("/");
    }
  }, [step, navigate]);

  const handleSubmit = async () => {
    if (!hasAnswer) return;
    setLoading(true);
    startLoadingTips();
    setError("");
    try {
      // Prepare answers - flatten complex types for backend
      const payload = { ...answers };
      if (Array.isArray(payload.shaft_likes_dislikes)) {
        payload.shaft_likes_dislikes = payload.shaft_likes_dislikes.join("; ");
      }
      // Flatten brand preference
      if (payload.brand_preference && typeof payload.brand_preference === "object" && !Array.isArray(payload.brand_preference)) {
        payload.preferred_brands = payload.brand_preference.preferred || "";
        payload.avoided_brands = payload.brand_preference.avoided || "";
        delete payload.brand_preference;
      } else if (typeof payload.brand_preference === "string") {
        // "No preference" skip
        payload.preferred_brands = "";
        payload.avoided_brands = "";
        delete payload.brand_preference;
      }
      // Extract contact info before cleaning payload
      const contactInfo = payload.contact_info || {};
      const customerName = contactInfo.name || "";
      const customerEmail = contactInfo.email || "";
      delete payload.contact_info;

      const requestBody = {
        answers: payload,
        customer_name: customerName,
        customer_email: customerEmail,
        tracking_id: trackingId || null,
        attribution,
      };

      // Try up to 2 attempts (auto-retry on failure)
      let res;
      let lastErr;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          res = await axios.post(`${API}/recommend`, requestBody, { timeout: 90000 });
          break;
        } catch (e) {
          lastErr = e;
          if (attempt === 0 && (!e.response || e.response.status >= 500 || e.code === "ECONNABORTED")) {
            // Auto-retry on server error or timeout
            continue;
          }
          throw e;
        }
      }
      if (!res) throw lastErr;

      // Fire Meta Pixel custom event only when email was provided
      if (customerEmail && typeof window.fbq === "function") {
        window.fbq("trackCustom", "G4LSurveyCompleted", { tool: "driver_shaft_quiz" });
      }

      sessionStorage.removeItem("quiz_tracking_id");
      navigate("/results", {
        state: {
          sessionId: res.data.session_id,
          recommendations: res.data.recommendations,
          generalAdvice: res.data.general_advice,
          productsFound: res.data.products_found,
          answers,
        },
      });
    } catch (err) {
      if (err.response?.status === 503) {
        setError("grips4less.com is temporarily busy. Please wait a moment and try again.");
      } else if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        setError("This is taking longer than usual. Please try again — it usually works on the second attempt.");
      } else {
        setError("Something went wrong. Please try again.");
      }
      if (process.env.NODE_ENV === "development") console.error(err);
    } finally {
      stopLoadingTips();
      setLoading(false);
    }
  };

  const isLast = step === questions.length - 1;

  const [loadingTipIndex, setLoadingTipIndex] = useState(0);

  const loadingTips = [
    { text: "Meaningful things take time...", sub: "We&#39;re scanning the largest shaft collection online" },
    { text: "Analyzing your swing profile...", sub: "Matching your speed, tempo, and preferences to real products" },
    { text: "Did you know?", sub: "Shaft flex affects accuracy more than distance for most golfers" },
    { text: "Almost there...", sub: "Our AI is comparing specs across dozens of shafts for you" },
    { text: "Fine-tuning your picks...", sub: "Making sure every recommendation fits your budget and goals" },
    { text: "Worth the wait...", sub: "A proper shaft fitting can add 10-15 yards off the tee" },
  ];

  const loadingIntervalRef = useRef(null);

  const startLoadingTips = useCallback(() => {
    setLoadingTipIndex(0);
    if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);
    let idx = 0;
    loadingIntervalRef.current = setInterval(() => {
      idx = (idx + 1) % 6;
      setLoadingTipIndex(idx);
    }, 3500);
  }, []);

  const stopLoadingTips = useCallback(() => {
    if (loadingIntervalRef.current) {
      clearInterval(loadingIntervalRef.current);
      loadingIntervalRef.current = null;
    }
  }, []);

  return (
    <div
      className="min-h-screen bg-[#03170D] flex flex-col items-center justify-center p-4"
      style={{
        backgroundImage:
          "radial-gradient(ellipse at 50% 0%, rgba(10,39,22,0.8) 0%, #03170D 70%)",
      }}
    >
      {loading && (
        <div className="fixed inset-0 z-50 bg-[#03170D] flex flex-col items-center justify-center p-6" style={{ backgroundImage: "radial-gradient(ellipse at 50% 0%, rgba(10,39,22,0.8) 0%, #03170D 70%)" }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center max-w-md text-center"
          >
            <div className="relative mb-8">
              <div className="w-20 h-20 rounded-full border-2 border-[#1A4026] flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-[#D4AF37] animate-pulse" />
              </div>
              <svg className="absolute inset-0 w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="38" fill="none" stroke="#D4AF37" strokeWidth="2" strokeDasharray="239" strokeDashoffset="239" strokeLinecap="round">
                  <animate attributeName="stroke-dashoffset" from="239" to="0" dur="25s" fill="freeze" />
                </circle>
              </svg>
            </div>
            <motion.div
              key={loadingTipIndex}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5 }}
            >
              <h2
                data-testid="loading-tip-title"
                className="text-2xl sm:text-3xl font-medium text-white mb-3"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {loadingTips[loadingTipIndex].text}
              </h2>
              <p
                className="text-[#A3B8AA] text-sm sm:text-base"
                style={{ fontFamily: "Outfit, sans-serif" }}
                dangerouslySetInnerHTML={{ __html: loadingTips[loadingTipIndex].sub }}
              />
            </motion.div>
            <div className="mt-10 flex gap-2">
              {loadingTips.map((_, i) => (
                <div
                  key={`dot-${i}`}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${i === loadingTipIndex ? "bg-[#D4AF37] w-6" : "bg-[#1A4026]"}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      )}
      {/* Progress */}
      <div className="w-full max-w-xl mb-8">
        <div className="flex items-center justify-between mb-3">
          <span
            data-testid="step-indicator"
            className="text-sm text-[#A3B8AA]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Step {step + 1} of {questions.length}
          </span>
          <span
            className="text-sm text-[#647D6C]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {Math.round(progress)}%
          </span>
        </div>
        <Progress
          data-testid="questionnaire-progress"
          value={progress}
          className="h-2 bg-[#1A4026]"
        />
      </div>

      {/* Question Card */}
      <div className="glass-card rounded-2xl w-full max-w-xl p-8 sm:p-10 relative overflow-hidden min-h-[480px] flex flex-col">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="flex-1"
          >
            <div className="flex items-start gap-2 mb-2">
              <h2
                data-testid={`question-title-${currentQ.id}`}
                className="text-xl sm:text-2xl tracking-tight leading-tight text-white"
              >
                {currentQ.question}
              </h2>
              {currentQ.hasTooltip && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid="swing-speed-tooltip"
                      className="mt-1 text-[#647D6C] hover:text-[#D4AF37] transition-colors flex-shrink-0"
                    >
                      <HelpCircle className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    className="bg-[#0A2716] border-[#1A4026] text-[#A3B8AA] max-w-xs p-4"
                    side="right"
                  >
                    <p
                      className="text-sm leading-relaxed"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      <strong className="text-white">
                        Not sure about your swing speed?
                      </strong>
                      <br />
                      Most golfers overestimate. Here&#39;s a rough guide:
                      <br />
                      <br />
                      <strong>Driver carry distance:</strong>
                      <br />
                      150-180 yds = ~75 mph
                      <br />
                      180-210 yds = ~85 mph
                      <br />
                      210-240 yds = ~95 mph
                      <br />
                      240-270 yds = ~105 mph
                      <br />
                      270+ yds = 105+ mph
                      <br />
                      <br />
                      For the most accurate reading, visit a local pro shop
                      with a launch monitor or use a swing speed app.
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {currentQ.subtitle && (
              <p
                className="text-sm text-[#647D6C] mb-6"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {currentQ.subtitle}
              </p>
            )}

            <div className="grid gap-2.5">
              {/* Text input question */}
              {currentQ.type === "text" && (
                <>
                  <input
                    data-testid={`text-input-${currentQ.id}`}
                    type="text"
                    value={typeof selectedValue === "string" ? selectedValue : ""}
                    onChange={handleTextChange}
                    placeholder={currentQ.placeholder || "Type here..."}
                    className="w-full px-5 py-4 rounded-xl border border-[#1A4026] bg-[rgba(10,39,22,0.4)] text-white placeholder-[#647D6C] outline-none focus:border-[#D4AF37] transition-colors"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                    autoFocus
                  />
                  {currentQ.skipLabel && (
                    <button
                      data-testid={`skip-${currentQ.id}`}
                      onClick={() => handleSkip(currentQ.skipValue)}
                      className={`option-card w-full text-left px-5 py-3.5 rounded-xl border transition-all ${
                        selectedValue === currentQ.skipValue
                          ? "selected border-[#D4AF37] bg-[rgba(212,175,55,0.15)]"
                          : "border-[#1A4026] bg-[rgba(10,39,22,0.4)] hover:border-[#D4AF37]/50"
                      }`}
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      <span className={`text-base ${selectedValue === currentQ.skipValue ? "text-white font-medium" : "text-[#647D6C]"}`}>
                        {currentQ.skipLabel}
                      </span>
                    </button>
                  )}
                </>
              )}

              {/* Contact info (name + email) */}
              {currentQ.type === "contact" && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm text-[#A3B8AA] block" style={{ fontFamily: "Outfit, sans-serif" }}>
                      Your first name
                    </label>
                    <input
                      data-testid="input-customer-name"
                      type="text"
                      value={(typeof selectedValue === "object" && selectedValue?.name) || ""}
                      onChange={(e) => setAnswers((prev) => ({
                        ...prev,
                        contact_info: { ...(prev.contact_info || {}), name: e.target.value },
                      }))}
                      placeholder="e.g., Mike"
                      className="w-full px-5 py-4 rounded-xl border border-[#1A4026] bg-[rgba(10,39,22,0.4)] text-white placeholder-[#647D6C] outline-none focus:border-[#D4AF37] transition-colors"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                      autoFocus
                    />
                  </div>
                  <div className="space-y-2 mt-3">
                    <label className="text-sm text-[#A3B8AA] block" style={{ fontFamily: "Outfit, sans-serif" }}>
                      Your email
                    </label>
                    <input
                      data-testid="input-customer-email"
                      type="email"
                      value={(typeof selectedValue === "object" && selectedValue?.email) || ""}
                      onChange={(e) => setAnswers((prev) => ({
                        ...prev,
                        contact_info: { ...(prev.contact_info || {}), email: e.target.value },
                      }))}
                      placeholder="e.g., mike@example.com"
                      className="w-full px-5 py-4 rounded-xl border border-[#1A4026] bg-[rgba(10,39,22,0.4)] text-white placeholder-[#647D6C] outline-none focus:border-[#D4AF37] transition-colors"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    />
                  </div>
                  <p className="text-xs text-[#647D6C] mt-3 text-center" style={{ fontFamily: "Outfit, sans-serif" }}>
                    We&#39;ll email your personalized shaft recommendations with direct links to shop. No spam, ever.
                  </p>
                </>
              )}

              {/* Multi-select question */}
              {currentQ.type === "multi" && (
                <>
                  <p className="text-xs text-[#647D6C] -mt-1 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Select all that apply
                  </p>
                  {currentOptions.map((opt) => {
                    const isSelected = Array.isArray(selectedValue) && selectedValue.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        data-testid={`option-${currentQ.id}-${opt.value.toLowerCase().replace(/[^a-z0-9]/g, "-").slice(0, 40)}`}
                        onClick={() => toggleMultiOption(opt.value)}
                        className={`option-card w-full text-left px-5 py-3 rounded-xl border transition-all ${
                          isSelected
                            ? "selected border-[#D4AF37] bg-[rgba(212,175,55,0.15)]"
                            : "border-[#1A4026] bg-[rgba(10,39,22,0.4)] hover:border-[#D4AF37]/50"
                        }`}
                        style={{ fontFamily: "Outfit, sans-serif" }}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            isSelected ? "border-[#D4AF37] bg-[#D4AF37]" : "border-[#647D6C]"
                          }`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-[#03170D]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <span className={`text-sm font-medium block ${isSelected ? "text-white" : "text-[#A3B8AA]"}`}>
                              {opt.label}
                            </span>
                            {opt.desc && (
                              <span className={`text-xs block mt-0.5 ${isSelected ? "text-[#A3B8AA]" : "text-[#647D6C]"}`}>
                                {opt.desc}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}

              {/* Brand preference question */}
              {currentQ.type === "brand_pref" && (
                <>
                  <div className="space-y-2">
                    <label className="text-sm text-[#A3B8AA] block" style={{ fontFamily: "Outfit, sans-serif" }}>
                      Brands I love or want to try
                    </label>
                    <input
                      data-testid="input-preferred-brands"
                      type="text"
                      value={typeof selectedValue === "object" && !Array.isArray(selectedValue) ? (selectedValue.preferred || "") : ""}
                      onChange={(e) => handleBrandChange("preferred", e.target.value)}
                      placeholder={currentQ.preferredPlaceholder}
                      className="w-full px-5 py-4 rounded-xl border border-[#1A4026] bg-[rgba(10,39,22,0.4)] text-white placeholder-[#647D6C] outline-none focus:border-[#D4AF37] transition-colors text-sm"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    />
                  </div>
                  <div className="space-y-2 mt-3">
                    <label className="text-sm text-[#A3B8AA] block" style={{ fontFamily: "Outfit, sans-serif" }}>
                      Brands I&#39;d rather avoid
                    </label>
                    <input
                      data-testid="input-avoided-brands"
                      type="text"
                      value={typeof selectedValue === "object" && !Array.isArray(selectedValue) ? (selectedValue.avoided || "") : ""}
                      onChange={(e) => handleBrandChange("avoided", e.target.value)}
                      placeholder={currentQ.avoidedPlaceholder}
                      className="w-full px-5 py-4 rounded-xl border border-[#1A4026] bg-[rgba(10,39,22,0.4)] text-white placeholder-[#647D6C] outline-none focus:border-[#D4AF37] transition-colors text-sm"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    />
                  </div>
                  <div className="mt-3 text-center">
                    <span className="text-xs text-[#647D6C] block mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>or</span>
                  </div>
                  <button
                    data-testid="skip-brand-preference"
                    onClick={() => handleSkip(currentQ.skipValue)}
                    className={`option-card w-full text-left px-5 py-3.5 rounded-xl border transition-all ${
                      selectedValue === currentQ.skipValue
                        ? "selected border-[#D4AF37] bg-[rgba(212,175,55,0.15)]"
                        : "border-[#1A4026] bg-[rgba(10,39,22,0.4)] hover:border-[#D4AF37]/50"
                    }`}
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    <span className={`text-sm ${selectedValue === currentQ.skipValue ? "text-white font-medium" : "text-[#647D6C]"}`}>
                      {currentQ.skipLabel}
                    </span>
                  </button>
                </>
              )}

              {/* Standard single-select question */}
              {!currentQ.type && currentOptions.map((opt) => (
                <button
                  key={opt.value}
                  data-testid={`option-${currentQ.id}-${opt.value
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, "-")}`}
                  onClick={() => selectOption(opt.value)}
                  className={`option-card w-full text-left px-5 py-3.5 rounded-xl border transition-all ${
                    selectedValue === opt.value
                      ? "selected border-[#D4AF37] bg-[rgba(212,175,55,0.15)]"
                      : "border-[#1A4026] bg-[rgba(10,39,22,0.4)] hover:border-[#D4AF37]/50"
                  }`}
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  <span
                    className={`text-base font-medium block ${
                      selectedValue === opt.value
                        ? "text-white"
                        : "text-[#A3B8AA]"
                    }`}
                  >
                    {opt.label}
                  </span>
                  {opt.desc && (
                    <span
                      className={`text-xs block mt-0.5 ${
                        selectedValue === opt.value
                          ? "text-[#A3B8AA]"
                          : "text-[#647D6C]"
                      }`}
                    >
                      {opt.desc}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {error && (
          <p
            data-testid="error-message"
            className="text-red-400 text-sm mt-4"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {error}
          </p>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 pt-5 border-t border-[#1A4026]">
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={goBack}
            className="text-[#A3B8AA] hover:text-white hover:bg-[rgba(255,255,255,0.05)]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          {isLast ? (
            <Button
              data-testid="submit-btn"
              onClick={handleSubmit}
              disabled={!hasAnswer || loading}
              className="bg-[#D4AF37] hover:bg-[#F0CC5C] text-[#03170D] font-semibold px-8 py-5 rounded-full disabled:opacity-40 transition-all hover:-translate-y-0.5"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing Your Game...
                </>
              ) : (
                <>
                  Find My Shafts
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          ) : (
            <Button
              data-testid="next-btn"
              onClick={goNext}
              disabled={!hasAnswer}
              className="bg-[#D4AF37] hover:bg-[#F0CC5C] text-[#03170D] font-semibold px-8 py-5 rounded-full disabled:opacity-40 transition-all hover:-translate-y-0.5"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
